import { useRef, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MdEmojiEvents, MdExpandMore, MdHistory, MdInfoOutline } from 'react-icons/md';
import DiceIcon from '../../images/dice-icon.svg?react';
import { RiftboundButton } from '../ui/button';
import { menuItemHoverBg, menuItemSelectedBg, menuItemSelectedHoverBg } from '../../theme/recipes/button';
import { dropdownBg, raisedShadow } from '../../theme/themeColor';
import { NavSubmenuPortal, useNavSubmenuHover } from './NavSubmenuPortal';

function PlaySubmenuLink({
  to,
  icon,
  active,
  children,
}: {
  to: string;
  icon: IconType | ReactNode;
  active: boolean;
  children: string;
}) {
  const iconNode =
    typeof icon === 'function' ? (
      <Icon as={icon} boxSize={4} color={active ? 'accent' : 'textMuted'} flexShrink={0} aria-hidden />
    ) : (
      <Box
        display="inline-flex"
        boxSize={4}
        color={active ? 'accent' : 'textMuted'}
        flexShrink={0}
        aria-hidden
        css={{ '& > svg': { width: '100%', height: '100%' } }}
      >
        {icon}
      </Box>
    );

  return (
    <RouterLink to={to} style={{ display: 'block', textDecoration: 'none', outline: 'none' }}>
      <Flex
        role="menuitem"
        align="center"
        gap={2.5}
        px={3}
        py={2}
        fontSize="sm"
        fontWeight={active ? 'semibold' : 'medium'}
        color={active ? 'accent' : 'text'}
        bg={active ? menuItemSelectedBg : 'transparent'}
        transitionProperty="background-color, color"
        transitionDuration="0.12s"
        _hover={{
          bg: active ? menuItemSelectedHoverBg : menuItemHoverBg,
          color: active ? 'accent' : 'text',
        }}
      >
        {iconNode}
        {children}
      </Flex>
    </RouterLink>
  );
}

export interface PlayNavSubmenuProps {
  /** Returns whether the given pathname belongs to the play section (for trigger active styling). */
  navMatch: (pathname: string) => boolean;
}

/** Desktop nav: Play trigger + quick routes for match tools. */
export function PlayNavSubmenu({ navMatch }: PlayNavSubmenuProps) {
  const location = useLocation();
  const hover = useNavSubmenuHover();
  const anchorRef = useRef<HTMLDivElement>(null);

  const pathname = location.pathname;
  const playSectionActive = navMatch(pathname);
  const newMatchActive = pathname === '/play/match';
  const landingActive = pathname === '/features/match-tracker';
  const historyActive = pathname.startsWith('/matches');
  const eventsActive = pathname.startsWith('/events');

  return (
    <Box
      ref={anchorRef}
      position="relative"
      onMouseEnter={hover.show}
      onMouseLeave={hover.hide}
    >
      <RiftboundButton
        as={RouterLink as never}
        {...{ to: '/play' } as Record<string, unknown>}
        treatment="ghost"
        size="sm"
        variant={playSectionActive ? 'primary' : undefined}
        px={{ md: 2, xl: 3 }}
        h="auto"
        py={1.5}
        aria-haspopup="menu"
        aria-expanded={hover.open}
      >
        <Flex align="center" gap={1} lineHeight={1.15}>
          <Text as="span" fontSize="sm">
            Play
          </Text>
          <Icon as={MdExpandMore} boxSize={3.5} opacity={0.75} pointerEvents="none" aria-hidden />
        </Flex>
      </RiftboundButton>

      <NavSubmenuPortal
        open={hover.open}
        anchorRef={anchorRef}
        label="Play submenu"
        minW="220px"
        onMouseEnter={hover.show}
        onMouseLeave={hover.hide}
      >
          <Box
            bg={dropdownBg}
            borderWidth="hairline"
            borderColor="border"
            rounded="md"
            py={1}
            boxShadow={raisedShadow}
          >
            <PlaySubmenuLink to="/play/match" icon={<DiceIcon />} active={newMatchActive}>
              New match
            </PlaySubmenuLink>
            <PlaySubmenuLink to="/matches" icon={MdHistory} active={historyActive}>
              Match history
            </PlaySubmenuLink>
            <PlaySubmenuLink to="/events" icon={MdEmojiEvents} active={eventsActive}>
              Events
            </PlaySubmenuLink>
            <PlaySubmenuLink to="/features/match-tracker" icon={MdInfoOutline} active={landingActive}>
              Play features
            </PlaySubmenuLink>
          </Box>
      </NavSubmenuPortal>
    </Box>
  );
}
